import { Container, Point } from "pixi.js";
import Layout from "../../src/utils/Layout";
import {PIXIElement} from "../../src/utils/Layout";
import { Tween } from "../Tween";
import { Ease } from "../Ease";
import { EaseType } from "../EaseType";

export function ScaleInFromCentre(object: Container, duration: number): Tween {
    object.scale.set(0, 0);

    const size = new Point(object.width, object.height);
    const offset = new Point(object.x, object.y);

    return Tween.to(object.scale, duration, {x: 1, y: 1, onUpdate: () => {
        object.x = (size.x - object.width) * 0.5 + offset.x;
        object.y = (size.y - object.height) * 0.5 + offset.y;
    }})

}

export function Rotate(object: Container, duration: number, radians: number, delay: number = 0): Tween {

    return Tween.to(object, duration, {
        rotation: radians,
        delay
    })
}

export function ZoomIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween {
    return Zoom(target, 0, 1, duration, delay, easing);
}

export function ZoomOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween {
    return Zoom(target, 1, 0, duration, delay, easing);
}

export function BounceZoomIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, startSize: number = 0): Tween {
    return Zoom(target, startSize, 1, duration, delay, Ease.BackOut);
}

export function BounceZoomOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0): Tween {
    return Zoom(target, 1, 0, duration, delay, Ease.BackIn);
}

export function SwipeFromTopIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween {
    return SwipeFromDirection(target, false, duration, delay, easing, 0);
}

export function SwipeFromTopOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween {
    return SwipeFromDirection(target, true, duration, delay, easing, 0);
}

export function SwipeFromLeftIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween {
    return SwipeFromDirection(target, false, duration, delay, easing, 1);
}

export function SwipeFromLeftOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween {
    return SwipeFromDirection(target, true, duration, delay, easing, 1);
}

export function SwipeFromBottomIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween  {
    return SwipeFromDirection(target, false, duration, delay, easing, 2);
}

export function SwipeFromBottomOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween  {
    return SwipeFromDirection(target, true, duration, delay, easing, 2);
}

export function SwipeFromRightIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween  {
    return SwipeFromDirection(target, false, duration, delay, easing, 3);
}

export function SwipeFromRightOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0, easing: EaseType = Ease.None): Tween  {
    return SwipeFromDirection(target, true, duration, delay, easing, 3);
}

export function FadeIn(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0): Tween  {
    return Fade(target, false, duration, delay);
}

export function FadeOut(target: PIXIElement | PIXIElement[], duration: number, delay: number = 0): Tween  {
    return Fade(target, true, duration, delay);
}


function Fade(targets: PIXIElement | PIXIElement[], out: boolean, duration: number, delay: number): Tween {
    const target = Layout.group(targets);
    target.alpha = out ? 1 : 0;
    const alphaAtEnd =  out ? 0 : 1;
    return Tween.to(
        target, duration, {
           alpha: alphaAtEnd,
           delay,
           onComplete: () => {
               if (target instanceof Container)  {
                   Layout.ungroup(target);
               }
           }
        }
    );

}

export function Blink(targets: PIXIElement | PIXIElement[], flickerCount: number, flickerDuration: number, delay: number): Tween {
    const target = Layout.group(targets);
    const opposite = target.alpha > 0 ? 0 : 1;

    return Tween.to(target, flickerDuration, {
        alpha: opposite,
        delay,
        yoyo: true,
        repeat: flickerCount,
        onComplete: () => {
            if (target instanceof Container)  {
                Layout.ungroup(target);
            }
        }

    })

}

function SwipeFromDirection(targets: PIXIElement | PIXIElement[], out: boolean, duration: number, delay: number, ease: EaseType, direction: number = 0 | 2): Tween {
    const target = Layout.group(targets);

    const offScreenPosition: Point = new Point(target.x, target.y);
    const destination: Point = new Point();

    const scalable = target as any;
    if (direction === 0) {
        // from top
        offScreenPosition.y = -scalable.height - target.parent.y;
    } else if (direction === 1) {
        // from left
        offScreenPosition.x = Layout.deviceWidth + target.parent.x;
    } else if (direction === 2) {
        // from bottom
        offScreenPosition.y = Layout.deviceHeight - target.parent.y;
    } else if (direction === 3) {
        // from right
        offScreenPosition.x = -scalable.width - target.parent.x;
    }
    destination.y = out ? offScreenPosition.y : target.y;
    destination.x = out ? offScreenPosition.x : target.x;

    if (!out) {
        target.y = offScreenPosition.y;
        target.x = offScreenPosition.x;
    }

    return Tween.to(target, duration, {
        y: destination.y,
        x: destination.x,
        delay,
        ease,
        onComplete: () => {
            if (target instanceof Container)  {
                Layout.ungroup(target);
            }
        }
    })

}

export function Zoom(targets: PIXIElement | PIXIElement[], scaleFrom: number, scaleTo: number, duration: number, delay: number, ease: EaseType = Ease.None): Tween {
    let target = Layout.centerPivot(targets);

    const tweenScale = new Point(scaleFrom * target.scale.x, scaleFrom * target.scale.y);
    const endScale = new Point(scaleTo * target.scale.x, scaleTo * target.scale.y);

    const onUpdate = () => {
        if (target && target.scale) {
            target.scale.set(tweenScale.x, tweenScale.y);
        }
    };

    onUpdate();

    return Tween.to(tweenScale, duration, {
        x: endScale.x,
        y: endScale.y,
        delay,
        onUpdate,
        onComplete: () => {
            if (target instanceof Container)  {
                Layout.ungroup(target);
            }
            if (scaleTo === 0) {
                if (targets && (targets as any).length) {
                    for (const groupedTarget of targets as PIXIElement[]) {
                        groupedTarget.scale.x = groupedTarget.scale.y = scaleTo;
                    }
                }
            }
            if (target.name === "tempContainer") {
                target = null;
            }

        }

    })

}

